import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Calculator, CreditCard, Send, Loader2, Phone, Mail, Trash2,
  Clock, CheckCircle2, RefreshCw, Inbox,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore } from '@/store';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACCOUNTING_PROGRAMS = ['Logo', 'Mikro', 'Netsis', 'Akınsoft Wolvox', 'Nebim', 'Paraşüt', 'Zirve', 'ETA', 'Luca', 'Diğer'];
const PAYMENT_PROVIDERS = ['Iyzico', 'PayTR', 'Param', 'Sipay', 'Kuveyt Türk Sanal POS', 'Garanti Sanal POS', 'İş Bankası Sanal POS', 'Stripe', 'PayPal', 'Diğer'];

const STATUS_META = {
  pending: { label: 'Beklemede', cls: 'bg-amber-50 text-amber-700 border-amber-200', icon: Clock },
  in_progress: { label: 'İnceleniyor', cls: 'bg-blue-50 text-blue-700 border-blue-200', icon: RefreshCw },
  done: { label: 'Tamamlandı', cls: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2 },
};

const TYPE_META = {
  accounting: { label: 'Muhasebe', cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
  payment: { label: 'Ödeme', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

function RequestForm({ type, icon: Icon, accent, title, desc, options, placeholder, token, onCreated }) {
  const { user } = useAuthStore();
  const [program, setProgram] = useState('');
  const [customProgram, setCustomProgram] = useState('');
  const [notes, setNotes] = useState('');
  const [contactPhone, setContactPhone] = useState(user?.phone || '');
  const [contactEmail, setContactEmail] = useState(user?.email || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const programName = program === 'Diğer' ? customProgram.trim() : program;
    if (!programName) { toast.error('Lütfen kullandığınız programı/altyapıyı belirtin'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/admin/integration-requests`, {
        type, program_name: programName, notes: notes.trim(),
        contact_phone: contactPhone.trim(), contact_email: contactEmail.trim(),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Destek kaydınız oluşturuldu. Ekibimiz en kısa sürede sizinle iletişime geçecek.');
      setProgram(''); setCustomProgram(''); setNotes('');
      onCreated && onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Destek kaydı oluşturulamadı');
    } finally { setSaving(false); }
  };

  return (
    <Card className="overflow-hidden" data-testid={`integration-form-${type}`}>
      <CardHeader className="bg-gray-50/70 border-b">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: accent.bg, color: accent.fg }}>
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <CardTitle className="text-base">{title}</CardTitle>
            <CardDescription className="text-xs">{desc}</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="p-4 space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Kullandığınız {type === 'accounting' ? 'muhasebe programı' : 'ödeme altyapısı'} *</Label>
          <Select value={program} onValueChange={setProgram}>
            <SelectTrigger className="w-full h-10 bg-white" data-testid={`${type}-program-select`}>
              <SelectValue placeholder={placeholder} />
            </SelectTrigger>
            <SelectContent>
              {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        {program === 'Diğer' && (
          <div className="space-y-1.5">
            <Label className="text-xs">Program / altyapı adı *</Label>
            <Input value={customProgram} onChange={(e) => setCustomProgram(e.target.value)} placeholder="Örn: Özel ERP" data-testid={`${type}-custom-input`} />
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">İletişim Telefonu</Label>
            <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="0XXX XXX XX XX" data-testid={`${type}-phone-input`} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">İletişim E-postası</Label>
            <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="ornek@firma.com" data-testid={`${type}-email-input`} />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Notlar (opsiyonel)</Label>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="Versiyon, sunucu bilgisi veya özel istekleriniz..." data-testid={`${type}-notes-input`} />
        </div>
        <Button onClick={submit} disabled={saving} className="w-full" data-testid={`${type}-submit-btn`}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
          Destek Talebi Oluştur
        </Button>
      </CardContent>
    </Card>
  );
}

export default function AdminIntegrationsPage() {
  const { token, user } = useAuthStore();
  const isSuperAdmin = user?.role === 'super_admin';
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/integration-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setRequests(res.data?.items || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchRequests(); }, []);

  const changeStatus = async (id, status) => {
    try {
      await axios.put(`${API_URL}/api/admin/integration-requests/${id}/status`, { status }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Durum güncellendi');
      fetchRequests();
    } catch (e) { toast.error(e.response?.data?.detail || 'Güncellenemedi'); }
  };

  const removeRequest = async (id) => {
    if (!window.confirm('Bu destek kaydını silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/api/admin/integration-requests/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Kayıt silindi');
      fetchRequests();
    } catch (e) { toast.error(e.response?.data?.detail || 'Silinemedi'); }
  };

  const fmtDate = (s) => { try { return new Date(s).toLocaleString('tr-TR', { dateStyle: 'medium', timeStyle: 'short' }); } catch { return s; } };
  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-6" data-testid="admin-integrations-page">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Entegrasyonlar</h1>
        <p className="text-sm text-gray-500">Muhasebe ve ödeme sistemleriniz için özel entegrasyon desteği talep edin.</p>
      </div>

      {/* Info banner */}
      <div className="rounded-xl border border-[#1a224f]/10 bg-[#1a224f]/[0.03] p-4 text-sm text-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p>
          Kullandığınız programı/altyapıyı belirtmeniz yeterli; ekibimiz size özel entegrasyon API’sini geliştirir.
          B2B özel geliştirme ve panel hakkında bilgi almak için bizimle iletişime geçebilirsiniz.
        </p>
        <div className="flex flex-wrap items-center gap-3 shrink-0">
          <a href="tel:+902166069924" className="inline-flex items-center gap-1.5 font-semibold text-[#1a224f] hover:underline" data-testid="integrations-contact-phone">
            <Phone className="h-4 w-4" /> +90 216 606 99 24
          </a>
          <a href="mailto:info@webplace.tr" className="inline-flex items-center gap-1.5 font-semibold text-[#1a224f] hover:underline" data-testid="integrations-contact-email">
            <Mail className="h-4 w-4" /> info@webplace.tr
          </a>
        </div>
      </div>

      {/* Request forms */}
      <div className="grid gap-4 lg:grid-cols-2">
        <RequestForm
          type="accounting" icon={Calculator}
          accent={{ bg: 'rgba(99,102,241,0.12)', fg: '#4f46e5' }}
          title="Muhasebe Entegrasyonu"
          desc="Muhasebe / ERP programınızı panele bağlayalım"
          options={ACCOUNTING_PROGRAMS}
          placeholder="Muhasebe programı seçin"
          token={token} onCreated={fetchRequests}
        />
        <RequestForm
          type="payment" icon={CreditCard}
          accent={{ bg: 'rgba(22,199,132,0.14)', fg: '#059669' }}
          title="Ödeme Sistemi Entegrasyonu"
          desc="Ödeme altyapınızı / sanal POS’unuzu bağlayalım"
          options={PAYMENT_PROVIDERS}
          placeholder="Ödeme altyapısı seçin"
          token={token} onCreated={fetchRequests}
        />
      </div>

      {/* Submitted requests */}
      <Card data-testid="integration-requests-list">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              Destek Kayıtları
              {pendingCount > 0 && <Badge className="bg-amber-100 text-amber-700 border-amber-200">{pendingCount} bekliyor</Badge>}
            </CardTitle>
            <CardDescription className="text-xs">Oluşturduğunuz entegrasyon talepleri ve durumları</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={fetchRequests} disabled={loading} data-testid="refresh-requests-btn">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </CardHeader>
        <CardContent>
          {requests.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <Inbox className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="text-sm">Henüz destek kaydı oluşturmadınız.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500">
                    <th className="py-2 pr-3 font-medium">Tür</th>
                    <th className="py-2 pr-3 font-medium">Program / Altyapı</th>
                    <th className="py-2 pr-3 font-medium hidden md:table-cell">Notlar</th>
                    <th className="py-2 pr-3 font-medium hidden sm:table-cell">Tarih</th>
                    <th className="py-2 pr-3 font-medium">Durum</th>
                    {isSuperAdmin && <th className="py-2 font-medium text-right">İşlem</th>}
                  </tr>
                </thead>
                <tbody>
                  {requests.map((r, idx) => {
                    const sm = STATUS_META[r.status] || STATUS_META.pending;
                    const tm = TYPE_META[r.type] || { label: r.type, cls: 'bg-gray-50 text-gray-700 border-gray-200' };
                    const SIcon = sm.icon;
                    return (
                      <tr key={r.id} className={`border-b last:border-0 ${idx % 2 ? 'bg-gray-50/40' : ''}`} data-testid={`request-row-${idx}`}>
                        <td className="py-3 pr-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium border ${tm.cls}`}>{tm.label}</span></td>
                        <td className="py-3 pr-3 font-medium text-gray-900">{r.program_name}</td>
                        <td className="py-3 pr-3 hidden md:table-cell text-gray-500 max-w-[220px] truncate">{r.notes || '—'}</td>
                        <td className="py-3 pr-3 hidden sm:table-cell text-gray-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                        <td className="py-3 pr-3">
                          {isSuperAdmin ? (
                            <Select value={r.status} onValueChange={(v) => changeStatus(r.id, v)}>
                              <SelectTrigger className="h-8 w-[130px] text-xs" data-testid={`status-select-${idx}`}><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="pending">Beklemede</SelectItem>
                                <SelectItem value="in_progress">İnceleniyor</SelectItem>
                                <SelectItem value="done">Tamamlandı</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium border ${sm.cls}`}>
                              <SIcon className="h-3 w-3" />{sm.label}
                            </span>
                          )}
                        </td>
                        {isSuperAdmin && (
                          <td className="py-3 text-right">
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-gray-400 hover:text-[#1a224f]" onClick={() => removeRequest(r.id)} data-testid={`delete-request-${idx}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
